// Pure orthogonal ("smooth step") routing used by the SVG/PDF export so the
// exported cable geometry matches the live canvas (React Flow's
// getSmoothStepPath) EXACTLY: same stub length, same side-aware bends, same
// handling of same-side and mixed-side handle pairs, same rounded corners.
//
// Sides are the stored docking sides: 'l' | 'r' | 't' | 'b'.

export const SIDE_DIR = {
  l: { x: -1, y: 0 },
  r: { x: 1, y: 0 },
  t: { x: 0, y: -1 },
  b: { x: 0, y: 1 },
};

export const STEP_OFFSET = 20; // React Flow default `offset` (stub length)
export const STEP_RADIUS = 6; // borderRadius used by ConnectionEdge

function getDirection(sourceGapped, sSide, targetGapped) {
  if (sSide === 'l' || sSide === 'r') {
    return sourceGapped.x < targetGapped.x ? { x: 1, y: 0 } : { x: -1, y: 0 };
  }
  return sourceGapped.y < targetGapped.y ? { x: 0, y: 1 } : { x: 0, y: -1 };
}

/**
 * Computes the polyline points of a smooth-step connection.
 * @param {{x:number,y:number}} source  handle anchor on the source device
 * @param {'l'|'r'|'t'|'b'} sSide
 * @param {{x:number,y:number}} target  handle anchor on the target device
 * @param {'l'|'r'|'t'|'b'} tSide
 * @param {{x?:number,y?:number}} center  optional lane override (centerX / centerY)
 * @param {number} offset stub length
 * @returns {Array<{x:number,y:number}>}
 */
export function smoothStepPoints(source, sSide, target, tSide, center = {}, offset = STEP_OFFSET) {
  const sourceDir = SIDE_DIR[sSide] || SIDE_DIR.r;
  const targetDir = SIDE_DIR[tSide] || SIDE_DIR.l;
  const sourceGapped = { x: source.x + sourceDir.x * offset, y: source.y + sourceDir.y * offset };
  const targetGapped = { x: target.x + targetDir.x * offset, y: target.y + targetDir.y * offset };
  const dir = getDirection(sourceGapped, sSide, targetGapped);
  const dirAccessor = dir.x !== 0 ? 'x' : 'y';
  const currDir = dir[dirAccessor];

  let points = [];
  const sourceGapOffset = { x: 0, y: 0 };
  const targetGapOffset = { x: 0, y: 0 };
  // React Flow (stepPosition 0.5): the default lane is the midpoint of the
  // stub-gapped endpoints.
  const defaultCenterX = (sourceGapped.x + targetGapped.x) / 2;
  const defaultCenterY = (sourceGapped.y + targetGapped.y) / 2;

  if (sourceDir[dirAccessor] * targetDir[dirAccessor] === -1) {
    // Opposite handle positions (e.g. bottom -> top, right -> left).
    const centerX = center.x ?? defaultCenterX;
    const centerY = center.y ?? defaultCenterY;
    const verticalSplit = [
      { x: centerX, y: sourceGapped.y },
      { x: centerX, y: targetGapped.y },
    ];
    const horizontalSplit = [
      { x: sourceGapped.x, y: centerY },
      { x: targetGapped.x, y: centerY },
    ];
    if (sourceDir[dirAccessor] === currDir) {
      points = dirAccessor === 'x' ? verticalSplit : horizontalSplit;
    } else {
      points = dirAccessor === 'x' ? horizontalSplit : verticalSplit;
    }
  } else {
    // Same-side or mixed (e.g. right -> bottom) handle positions.
    const sourceTarget = [{ x: sourceGapped.x, y: targetGapped.y }];
    const targetSource = [{ x: targetGapped.x, y: sourceGapped.y }];
    if (dirAccessor === 'x') {
      points = sourceDir.x === currDir ? targetSource : sourceTarget;
    } else {
      points = sourceDir.y === currDir ? sourceTarget : targetSource;
    }
    if (sSide === tSide) {
      const diff = Math.abs(source[dirAccessor] - target[dirAccessor]);
      if (diff <= offset) {
        const gapOffset = Math.min(offset - 1, offset - diff);
        if (sourceDir[dirAccessor] === currDir) {
          sourceGapOffset[dirAccessor] = (sourceGapped[dirAccessor] > source[dirAccessor] ? -1 : 1) * gapOffset;
        } else {
          targetGapOffset[dirAccessor] = (targetGapped[dirAccessor] > target[dirAccessor] ? -1 : 1) * gapOffset;
        }
      }
    }
    if (sSide !== tSide) {
      const dirAccessorOpposite = dirAccessor === 'x' ? 'y' : 'x';
      const isSameDir = sourceDir[dirAccessor] === targetDir[dirAccessorOpposite];
      const sourceGtTargetOppo = sourceGapped[dirAccessorOpposite] > targetGapped[dirAccessorOpposite];
      const sourceLtTargetOppo = sourceGapped[dirAccessorOpposite] < targetGapped[dirAccessorOpposite];
      const flipSourceTarget =
        (sourceDir[dirAccessor] === 1 && ((!isSameDir && sourceGtTargetOppo) || (isSameDir && sourceLtTargetOppo))) ||
        (sourceDir[dirAccessor] !== 1 && ((!isSameDir && sourceLtTargetOppo) || (isSameDir && sourceGtTargetOppo)));
      if (flipSourceTarget) {
        points = dirAccessor === 'x' ? sourceTarget : targetSource;
      }
    }
  }

  return [
    { x: source.x, y: source.y },
    { x: sourceGapped.x + sourceGapOffset.x, y: sourceGapped.y + sourceGapOffset.y },
    ...points,
    { x: targetGapped.x + targetGapOffset.x, y: targetGapped.y + targetGapOffset.y },
    { x: target.x, y: target.y },
  ];
}

const fmt = (n) => (Math.round(n * 100) / 100).toString();

function bend(a, b, c, size) {
  const bendSize = Math.min(dist(a, b) / 2, dist(b, c) / 2, size);
  const { x, y } = b;
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return `L${fmt(x)} ${fmt(y)}`;
  }
  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1;
    const yDir = a.y < c.y ? 1 : -1;
    return `L ${fmt(x + bendSize * xDir)},${fmt(y)}Q ${fmt(x)},${fmt(y)} ${fmt(x)},${fmt(y + bendSize * yDir)}`;
  }
  const xDir = a.x < c.x ? 1 : -1;
  const yDir = a.y < c.y ? -1 : 1;
  return `L ${fmt(x)},${fmt(y + bendSize * yDir)}Q ${fmt(x)},${fmt(y)} ${fmt(x + bendSize * xDir)},${fmt(y)}`;
}

function dist(a, b) {
  return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
}

/** SVG path `d` with rounded corners for a polyline (React Flow getBend port). */
export function pointsToRoundedPath(points, radius = STEP_RADIUS) {
  return points.reduce((res, p, i) => {
    let segment;
    if (i > 0 && i < points.length - 1) segment = bend(points[i - 1], p, points[i + 1], radius);
    else segment = `${i === 0 ? 'M' : 'L'}${fmt(p.x)} ${fmt(p.y)}`;
    return res + segment;
  }, '');
}

/**
 * Lane stagger for parallel cables sharing the same endpoints, mirroring
 * ConnectionEdge: the centre lane is clamped to the corridor between the
 * stub-gapped endpoints so a stagger can never bend the cable backwards.
 */
export function laneCenter(source, sSide, target, tSide, midOff, offset = STEP_OFFSET) {
  const horizontal = sSide === 'l' || sSide === 'r';
  if (horizontal) {
    const sg = source.x + (sSide === 'l' ? -offset : offset);
    const tg = target.x + (tSide === 'l' ? -offset : offset);
    const lo = Math.min(sg, tg);
    const hi = Math.max(sg, tg);
    return { x: Math.max(lo, Math.min(hi, (source.x + target.x) / 2 + midOff)) };
  }
  const sg = source.y + (sSide === 't' ? -offset : offset);
  const tg = target.y + (tSide === 't' ? -offset : offset);
  const lo = Math.min(sg, tg);
  const hi = Math.max(sg, tg);
  return { y: Math.max(lo, Math.min(hi, (source.y + target.y) / 2 + midOff)) };
}
